import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChartBarIcon,
  BanknotesIcon,
  CubeIcon,
  UsersIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  FunnelIcon,
  XMarkIcon,
  PrinterIcon,
  TruckIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline'
import { reportService } from '../../services/reportService'
import { customerService } from '../../services/customerService'
import { productService } from '../../services/productService'
import dayjs from 'dayjs'

type ReportType = 'sales' | 'inventory' | 'customers' | 'financial' | 'products' | 'stock' | 'reversals' | 'consignment' | null

interface Filters {
  startDate: string
  endDate: string
  productId: string
  customerId: string
  groupId: string
  categoryId: string
  paymentStatus: string
  period: 'daily' | 'weekly' | 'monthly'
}

export const ReportsPage = () => {
  const [activeReport, setActiveReport] = useState<ReportType>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState<Filters>({
    startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    productId: '',
    customerId: '',
    groupId: '',
    categoryId: '',
    paymentStatus: '',
    period: 'daily',
  })

  // Fetch filter options
  const { data: productsData } = useQuery({
    queryKey: ['products-dropdown'],
    queryFn: () => productService.getAll({ limit: 1000 }),
  })

  const { data: customersData } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: () => customerService.getAll({ limit: 1000 }),
  })

  const { data: groupsData } = useQuery({
    queryKey: ['customer-groups'],
    queryFn: () => customerService.getGroups(),
  })

  const { data: categoriesData } = useQuery({
    queryKey: ['categories-dropdown'],
    queryFn: () => productService.getCategories(),
  })

  // Fetch reports based on active selection
  const { data: salesReport, isLoading: loadingSales } = useQuery({
    queryKey: ['reports', 'sales', filters],
    queryFn: () => reportService.getSalesReport({
      startDate: filters.startDate,
      endDate: filters.endDate,
      period: filters.period,
    }),
    enabled: activeReport === 'sales' || activeReport === null,
  })

  const { data: inventoryReport, isLoading: loadingInventory } = useQuery({
    queryKey: ['reports', 'inventory'],
    queryFn: () => reportService.getInventoryReport(),
    enabled: activeReport === 'inventory' || activeReport === null,
  })

  const { data: customerReport, isLoading: loadingCustomers } = useQuery({
    queryKey: ['reports', 'customers'],
    queryFn: () => reportService.getCustomerReport(),
    enabled: activeReport === 'customers' || activeReport === null,
  })

  const { data: profitReport, isLoading: loadingFinancial } = useQuery({
    queryKey: ['reports', 'profit', filters],
    queryFn: () => reportService.getProfitReport({
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    enabled: activeReport === 'financial',
  })

  const { data: productReport, isLoading: loadingProducts } = useQuery({
    queryKey: ['reports', 'products'],
    queryFn: () => reportService.getProductReport(),
    enabled: activeReport === 'products',
  })

  const { data: stockReport, isLoading: loadingStock } = useQuery({
    queryKey: ['reports', 'stock'],
    queryFn: () => reportService.getStockReport(),
    enabled: activeReport === 'stock',
  })

  const { data: reversalReport, isLoading: loadingReversals } = useQuery({
    queryKey: ['reports', 'reversals', filters],
    queryFn: () => reportService.getReversalReport({
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    enabled: activeReport === 'reversals',
  })

  const { data: consignmentReportData, isLoading: loadingConsignment } = useQuery({
    queryKey: ['reports', 'consignment', filters],
    queryFn: () => reportService.getConsignmentReport({
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    enabled: activeReport === 'consignment',
  })

  const products = Array.isArray(productsData?.data?.data?.items) ? productsData?.data?.data?.items : []
  const customers = Array.isArray(customersData?.data?.data?.items) ? customersData?.data?.data?.items : []
  const groups = Array.isArray(groupsData?.data?.data) ? groupsData?.data?.data : []
  const categories = Array.isArray(categoriesData?.data?.data) ? categoriesData?.data?.data : []

  const salesData = salesReport?.data?.data
  const inventoryData = inventoryReport?.data?.data
  const customerData = customerReport?.data?.data
  const financialData = profitReport?.data?.data
  const productData = productReport?.data?.data
  const stockData = stockReport?.data?.data
  const reversalData = reversalReport?.data?.data
  const consignmentData = consignmentReportData?.data?.data

  const handleExport = () => {
    // Create CSV content based on active report
    let csvContent = ''
    let filename = ''

    if (activeReport === 'sales' && salesData) {
      filename = `sales-report-${filters.startDate}-to-${filters.endDate}.csv`
      csvContent = 'Sale Number,Date,Customer,Total,Status\n'
      csvContent += salesData.sales?.map((s: any) => 
        `"${s.sale_number}","${dayjs(s.sale_date).format('YYYY-MM-DD')}","${s.customer}",${s.total_amount},${s.status}`
      ).join('\n') || ''
    } else if (activeReport === 'financial' && financialData) {
      filename = `financial-report-${filters.startDate}-to-${filters.endDate}.csv`
      csvContent = 'Product,Quantity,Revenue,Cost,Profit,Margin%\n'
      csvContent += financialData.item_profits?.map((i: any) =>
        `"${i.product_name}",${i.quantity},${i.revenue},${i.cost},${i.profit},${i.margin_percent}`
      ).join('\n') || ''
    }

    if (csvContent) {
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
    }
  }

  const renderSalesReport = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Sales</p>
          <p className="text-xl font-bold">KSh {Number(salesData?.summary?.total_sales || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Orders</p>
          <p className="text-xl font-bold">{Number(salesData?.summary?.total_orders || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Items Sold</p>
          <p className="text-xl font-bold">{Number(salesData?.summary?.total_items || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Avg Order Value</p>
          <p className="text-xl font-bold">KSh {Number(salesData?.summary?.average_order_value || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* Sales by Category & Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Sales by Category</h3>
          <div className="space-y-3">
            {salesData?.category_sales?.map((cat: any, idx: number) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{cat.category}</span>
                <div className="text-right">
                  <p className="text-sm font-medium">KSh {Number(cat.revenue).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">{cat.units} units</p>
                </div>
              </div>
            ))}
            {!salesData?.category_sales?.length && (
              <p className="text-gray-500 text-center py-4">No category data</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Top Products</h3>
          <div className="space-y-3">
            {salesData?.top_products?.slice(0, 10).map((product: any, idx: number) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-xs font-medium text-primary-600">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{product.product_name}</p>
                  <p className="text-xs text-gray-500">{product.sku} • {product.quantity} sold</p>
                </div>
                <p className="text-sm font-medium">KSh {Number(product.revenue).toLocaleString()}</p>
              </div>
            ))}
            {!salesData?.top_products?.length && (
              <p className="text-gray-500 text-center py-4">No product data</p>
            )}
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <h3 className="font-semibold text-gray-900 p-6 border-b">Sales Transactions</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {salesData?.sales?.map((sale: any) => (
                <tr key={sale.sale_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{sale.sale_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{dayjs(sale.sale_date).format('MMM D, YYYY')}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{sale.customer}</td>
                  <td className="px-6 py-4 text-sm font-medium text-right">KSh {Number(sale.total_amount).toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      sale.status === 'paid' ? 'bg-green-100 text-green-800' :
                      sale.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {sale.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderInventoryReport = () => {
    const categories = inventoryData?.categories || []
    const lowStock = inventoryData?.low_stock || []
    const summary = inventoryData?.summary || {}

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Inventory by Category */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Inventory by Category</h3>
            <div className="space-y-3">
              {categories.map((item: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{item.category}</span>
                  <div className="text-right">
                    <p className="text-sm font-medium">{item.product_count} products</p>
                    <p className="text-xs text-gray-500">{item.unit_count} units</p>
                  </div>
                </div>
              ))}
              {!categories.length && (
                <p className="text-gray-500 text-center py-4">No inventory data</p>
              )}
            </div>
          </div>

          {/* Stock Status */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Stock Status</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                <span className="text-sm text-green-800">In Stock Products</span>
                <span className="text-lg font-bold text-green-800">
                  {summary.total_products || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                <span className="text-sm text-yellow-800">Low Stock</span>
                <span className="text-lg font-bold text-yellow-800">
                  {summary.low_stock_count || 0}
                </span>
              </div>
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <span className="text-sm text-blue-800">Total Units</span>
                <span className="text-lg font-bold text-blue-800">
                  {summary.total_units || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Inventory Value */}
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Inventory Value</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Value (Cost)</span>
                <span className="font-medium">
                  KSh {Number(summary.total_value || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total Units</span>
                <span className="font-medium">
                  {Number(summary.total_units || 0).toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Categories</span>
                <span className="font-medium">{summary.total_categories || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Low Stock Items */}
        {lowStock.length > 0 && (
          <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Low Stock Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Product</th>
                    <th className="text-left px-4 py-2 font-medium">SKU</th>
                    <th className="text-right px-4 py-2 font-medium">Available</th>
                    <th className="text-right px-4 py-2 font-medium">Reorder Level</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {lowStock.map((item: any) => (
                    <tr key={item.product_id}>
                      <td className="px-4 py-3">{item.product_name}</td>
                      <td className="px-4 py-3 text-gray-500">{item.sku}</td>
                      <td className="px-4 py-3 text-right font-medium text-yellow-600">
                        {item.available_count}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500">
                        {item.reorder_level}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    )
  }

  const renderCustomerReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Customers */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Top Customers by Revenue</h3>
          <div className="space-y-3">
            {customerData?.top_customers?.slice(0, 10).map((customer: any, idx: number) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-xs font-medium text-green-600">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {customer.customer_name || customer.name || customer.company_name}
                  </p>
                  <p className="text-xs text-gray-500">{customer.order_count || 0} orders</p>
                </div>
                <p className="text-sm font-medium">KSh {Number(customer.total_purchases || customer.amount).toLocaleString()}</p>
              </div>
            ))}
            {!customerData?.top_customers?.length && (
              <p className="text-gray-500 text-center py-4">No customer data</p>
            )}
          </div>
        </div>

        {/* Customer Groups */}
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Customers by Group</h3>
          <div className="space-y-3">
            {customerData?.by_group?.map((group: any, idx: number) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-sm text-gray-700">{group.group_name}</span>
                <div className="text-right">
                  <p className="text-sm font-medium">{group.customer_count} customers</p>
                  <p className="text-xs text-gray-500">KSh {Number(group.total_revenue || 0).toLocaleString()}</p>
                </div>
              </div>
            ))}
            {!customerData?.by_group?.length && (
              <p className="text-gray-500 text-center py-4">No group data</p>
            )}
          </div>
        </div>
      </div>

      {/* All Customers Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <h3 className="font-semibold text-gray-900 p-6 border-b">Customer Details</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Group</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Purchases</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {customerData?.customers?.map((customer: any) => (
                <tr key={customer.customer_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">
                      {customer.company_name || `${customer.first_name} ${customer.last_name}`}
                    </p>
                    <p className="text-xs text-gray-500">{customer.email}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{customer.group_name || 'Retail'}</td>
                  <td className="px-6 py-4 text-sm text-right">{customer.order_count || 0}</td>
                  <td className="px-6 py-4 text-sm font-medium text-right">
                    KSh {Number(customer.total_purchases || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    <span className={customer.current_balance > 0 ? 'text-red-600 font-medium' : 'text-green-600'}>
                      KSh {Number(customer.current_balance || 0).toLocaleString()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderFinancialReport = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Revenue</p>
          <p className="text-xl font-bold text-green-600">
            KSh {Number(financialData?.summary?.total_revenue || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Cost</p>
          <p className="text-xl font-bold text-red-600">
            KSh {Number(financialData?.summary?.total_cost || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Profit</p>
          <p className="text-xl font-bold text-blue-600">
            KSh {Number(financialData?.summary?.total_profit || 0).toLocaleString()}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Overall Margin</p>
          <p className="text-xl font-bold text-purple-600">
            {financialData?.summary?.overall_margin_percent || 0}%
          </p>
        </div>
      </div>

      {/* Profit by Product */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <h3 className="font-semibold text-gray-900 p-6 border-b">Profit by Product</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Margin</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {financialData?.item_profits?.map((item: any, idx: number) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.product_name}</td>
                  <td className="px-6 py-4 text-sm text-right">{item.quantity}</td>
                  <td className="px-6 py-4 text-sm text-right">KSh {Number(item.revenue).toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-right">KSh {Number(item.cost).toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm font-medium text-right text-blue-600">
                    KSh {Number(item.profit).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      Number(item.margin_percent) > 30 ? 'bg-green-100 text-green-800' :
                      Number(item.margin_percent) > 15 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {item.margin_percent}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderProductReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Products</p>
          <p className="text-xl font-bold">{Number(productData?.summary?.total_products || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Stock Value</p>
          <p className="text-xl font-bold">KSh {Number(productData?.summary?.total_stock_value || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Potential Revenue</p>
          <p className="text-xl font-bold">KSh {Number(productData?.summary?.total_potential_revenue || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Low Stock</p>
          <p className="text-xl font-bold">{Number(productData?.summary?.low_stock_count || 0).toLocaleString()}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cost</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Selling</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Latest Supplier</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {productData?.products?.map((p: any) => (
              <tr key={p.product_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{p.product_name}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{p.sku}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{p.brand}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{p.category}</td>
                <td className="px-6 py-4 text-sm text-right">KSh {Number(p.cost_price).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right">KSh {Number(p.selling_price).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-right">{p.stock_count}</td>
                <td className="px-6 py-4 text-sm">
                  {p.latest_supplier ? (
                    <div>
                      <p className="font-medium text-gray-900">{p.latest_supplier.company_name}</p>
                      <p className="text-xs text-gray-500">{p.latest_supplier.supplier_code}</p>
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-right font-medium">KSh {Number(p.stock_value).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderStockReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Products</p>
          <p className="text-xl font-bold">{Number(stockData?.summary?.total_products || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">In Stock</p>
          <p className="text-xl font-bold text-green-600">{Number(stockData?.summary?.in_stock || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Low Stock</p>
          <p className="text-xl font-bold text-yellow-600">{Number(stockData?.summary?.low_stock || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Out of Stock</p>
          <p className="text-xl font-bold text-red-600">{Number(stockData?.summary?.out_of_stock || 0).toLocaleString()}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Available</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Reorder Level</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Latest Supplier</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Stock Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {stockData?.stock?.map((s: any) => (
              <tr key={s.product_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{s.product_name}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{s.sku}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{s.brand}</td>
                <td className="px-6 py-4 text-sm text-right">{s.available_stock}</td>
                <td className="px-6 py-4 text-sm text-right">{s.reorder_level}</td>
                <td className="px-6 py-4 text-sm">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    s.status === 'in_stock' ? 'bg-green-100 text-green-800' :
                    s.status === 'low_stock' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {s.status.replace('_', ' ')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm">
                  {s.latest_supplier ? (
                    <div>
                      <p className="font-medium text-gray-900">{s.latest_supplier.company_name}</p>
                      <p className="text-xs text-gray-500">{s.latest_supplier.supplier_code}</p>
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-right font-medium">KSh {Number(s.stock_value).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderReversalReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Reversals</p>
          <p className="text-xl font-bold">{Number(reversalData?.summary?.total_reversals || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Value Reversed</p>
          <p className="text-xl font-bold">KSh {Number(reversalData?.summary?.total_value_reversed || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Items Reversed</p>
          <p className="text-xl font-bold">{Number(reversalData?.summary?.total_items_reversed || 0).toLocaleString()}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sale</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reversed By</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {reversalData?.reversals?.map((r: any) => (
              <tr key={r.reversal_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{r.sale_number}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{r.customer}</td>
                <td className="px-6 py-4 text-sm text-right">KSh {Number(r.total_amount).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{r.reason}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{r.reversed_by}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{new Date(r.reversed_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  const renderConsignmentReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Total Consignments</p>
          <p className="text-xl font-bold">{Number(consignmentData?.summary?.total_consignments || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Active</p>
          <p className="text-xl font-bold text-blue-600">{Number(consignmentData?.summary?.active_consignments || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Units Issued</p>
          <p className="text-xl font-bold">{Number(consignmentData?.summary?.total_units_issued || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl shadow border border-gray-100 p-4">
          <p className="text-sm text-gray-500">Sales Value</p>
          <p className="text-xl font-bold text-green-600">KSh {Number(consignmentData?.summary?.total_sales_value || 0).toLocaleString()}</p>
        </div>
      </div>
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Consignment</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Issued</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sold</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Returned</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Sales Value</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {consignmentData?.consignments?.map((c: any) => (
              <tr key={c.consignment_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{c.consignment_number}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{c.partner}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{new Date(c.issue_date).toLocaleDateString()}</td>
                <td className="px-6 py-4 text-sm text-right">{c.units_issued}</td>
                <td className="px-6 py-4 text-sm text-right">{c.units_sold}</td>
                <td className="px-6 py-4 text-sm text-right">{c.units_returned}</td>
                <td className="px-6 py-4 text-sm text-right font-medium">KSh {Number(c.sales_value).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm">
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    c.status === 'active' ? 'bg-green-100 text-green-800' :
                    c.status === 'partially_sold' ? 'bg-yellow-100 text-yellow-800' :
                    c.status === 'fully_sold' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {c.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">
            View business performance and insights
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              showFilters ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-300 hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="w-5 h-5" />
            Filters
            {Object.values(filters).some(v => v && v !== 'daily') && (
              <span className="w-2 h-2 bg-primary-600 rounded-full" />
            )}
          </button>
          {activeReport && (
            <>
              <button
                onClick={handleExport}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <DocumentArrowDownIcon className="w-5 h-5" />
                Export CSV
              </button>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <PrinterIcon className="w-5 h-5" />
                Print
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <FunnelIcon className="w-5 h-5" />
              Report Filters
            </h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <div className="relative">
                <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
              <select
                value={filters.period}
                onChange={(e) => setFilters(f => ({ ...f, period: e.target.value as any }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            {/* Payment Status */}
            {(activeReport === 'sales' || activeReport === 'financial') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => setFilters(f => ({ ...f, paymentStatus: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Statuses</option>
                  <option value="paid">Paid</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
            )}

            {/* Product */}
            {activeReport !== 'customers' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                <select
                  value={filters.productId}
                  onChange={(e) => setFilters(f => ({ ...f, productId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Products</option>
                  {products.map((p: any) => (
                    <option key={p.product_id} value={p.product_id}>{p.product_name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Category */}
            {activeReport !== 'customers' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product Category</label>
                <select
                  value={filters.categoryId}
                  onChange={(e) => setFilters(f => ({ ...f, categoryId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Categories</option>
                  {categories.map((c: any) => (
                    <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Customer */}
            {activeReport !== 'inventory' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer</label>
                <select
                  value={filters.customerId}
                  onChange={(e) => setFilters(f => ({ ...f, customerId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Customers</option>
                  {customers.map((c: any) => (
                    <option key={c.customer_id} value={c.customer_id}>
                      {c.company_name || `${c.first_name} ${c.last_name}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Customer Group */}
            {activeReport !== 'inventory' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Group</label>
                <select
                  value={filters.groupId}
                  onChange={(e) => setFilters(f => ({ ...f, groupId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Groups</option>
                  {groups.map((g: any) => (
                    <option key={g.group_id} value={g.group_id}>{g.group_name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => setFilters({
                startDate: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
                endDate: dayjs().format('YYYY-MM-DD'),
                productId: '',
                customerId: '',
                groupId: '',
                categoryId: '',
                paymentStatus: '',
                period: 'daily',
              })}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Reset Filters
            </button>
            <button
              onClick={() => setShowFilters(false)}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Report Selection Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { name: 'Sales Report', id: 'sales', icon: BanknotesIcon, color: 'bg-primary-100 text-primary-600', desc: 'Sales, orders, top products' },
          { name: 'Inventory Report', id: 'inventory', icon: CubeIcon, color: 'bg-green-100 text-green-600', desc: 'Stock levels, categories' },
          { name: 'Customer Report', id: 'customers', icon: UsersIcon, color: 'bg-blue-100 text-blue-600', desc: 'Customers, groups, purchases' },
          { name: 'Financial Report', id: 'financial', icon: ChartBarIcon, color: 'bg-purple-100 text-purple-600', desc: 'Profit, margins, revenue' },
          { name: 'Product Report', id: 'products', icon: CubeIcon, color: 'bg-amber-100 text-amber-600', desc: 'All products with pricing' },
          { name: 'Stock Report', id: 'stock', icon: CubeIcon, color: 'bg-red-100 text-red-600', desc: 'Current stock status' },
          { name: 'Consignment Report', id: 'consignment', icon: TruckIcon, color: 'bg-indigo-100 text-indigo-600', desc: 'Consignment performance' },
          { name: 'Reversal Report', id: 'reversals', icon: ArrowUturnLeftIcon, color: 'bg-rose-100 text-rose-600', desc: 'Sale reversals log' },
        ].map((report) => (
          <button
            key={report.id}
            onClick={() => setActiveReport(report.id as ReportType)}
            className={`flex flex-col p-4 rounded-xl shadow border transition-all text-left ${
              activeReport === report.id 
                ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200' 
                : 'bg-white border-gray-100 hover:shadow-md'
            }`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={`p-2 rounded-lg ${report.color}`}>
                <report.icon className="w-5 h-5" />
              </div>
              <span className="font-medium text-gray-900">{report.name}</span>
            </div>
            <p className="text-xs text-gray-500 ml-10">{report.desc}</p>
          </button>
        ))}
      </div>

      {/* Report Content */}
      {activeReport === 'sales' && (loadingSales ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderSalesReport())}

      {activeReport === 'inventory' && (loadingInventory ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderInventoryReport())}

      {activeReport === 'customers' && (loadingCustomers ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderCustomerReport())}

      {activeReport === 'financial' && (loadingFinancial ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderFinancialReport())}

      {activeReport === 'products' && (loadingProducts ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderProductReport())}

      {activeReport === 'stock' && (loadingStock ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderStockReport())}

      {activeReport === 'reversals' && (loadingReversals ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderReversalReport())}

      {activeReport === 'consignment' && (loadingConsignment ? (
        <div className="flex justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : renderConsignmentReport())}

      {!activeReport && (
        <div className="bg-white rounded-xl shadow border border-gray-100 p-12 text-center">
          <ChartBarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Report</h3>
          <p className="text-gray-500">Choose a report type above to view detailed analytics</p>
        </div>
      )}
    </div>
  )
}
